import React from 'react'

import './AboutModal.scss'
import psLogo from './ps-logo.png'
import NewTabLink from '../Helpers/NewTabLink'

class AboutModal extends React.Component {
  public render(): JSX.Element {
    return (
      <div
        className="AboutModal modal fade"
        id="AboutModal"
        role="dialog"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title" id="AboutModalTitle">
                About the Canadian Election Explorer
              </h2>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <p>
                Canadians are heading to the polls on October 21, 2019 to elect
                their Members of Parliament and to see which party will form
                government. The 2019 Canadian Election Explorer lets you explore
                possible outcomes of the election given differences in polling
                support for the various parties relative to 2015.
              </p>
              <h4>Who made this?</h4>
              <div className="row">
                <div className="col-8">
                  <p>
                    The 2019 Canadian Election Explorer is by{' '}
                    <NewTabLink href="https://plotandscatter.com">
                      Plot + Scatter
                    </NewTabLink>
                    , a Vancouver-based data communication company. It is an
                    all-new version of our{' '}
                    <NewTabLink href="https://plotandscatter.com/canada-election-explorer">
                      2015 Canadian Election Explorer
                    </NewTabLink>
                    , and also incorporates ideas from our{' '}
                    <NewTabLink href="https://plotandscatter.com/bc-elections-2017">
                      2017 BC Election app.
                    </NewTabLink>
                  </p>
                </div>
                <div className="col-4">
                  <NewTabLink href="https://plotandscatter.com">
                    <img
                      src={psLogo}
                      className="img-fluid"
                      alt="Plot + Scatter logo"
                    />
                  </NewTabLink>
                </div>
              </div>
              <h4>How does it work?</h4>
              <p>
                Initially, all values in the tool (seat counts and vote
                percentages) are set to the actual results of the most recent
                federal election in 2015. (By-elections since then are not taken
                into account.)
              </p>
              <p>
                Using the slider inputs, you can adjust party support levels
                either in the country as a whole or within each region &mdash;
                use the dropdown next to the slider to switch. If you want some
                ideas of current polled party support, check out a site like the{' '}
                <NewTabLink href="https://newsinteractives.cbc.ca/elections/poll-tracker/canada/">
                  CBC Poll Tracker
                </NewTabLink>
                .
              </p>
              <p>
                As you move the sliders, the app will build and show a
                prediction for the coming election based on those polling
                inputs. The prediction methodology will use either a{' '}
                <strong>deterministic swing model</strong> or a{' '}
                <strong>random swing model</strong>, based on your selection in
                the Settings dropdown.
              </p>
              <h5>Deterministic swing model</h5>
              <p>
                In this model, for each riding in a given region, we apply the
                difference between a party&rsquo;s vote share in that region in
                the previous election and its predicted vote share in this
                election. If you enter only national results, the swing in each
                region is weighted proportionately to the support of that party
                in the previous election.
              </p>
              <p>
                For example, if the national polling slider is set to indicate
                nation-wide Green support of 10.5% in this election, and the
                actual nation-wide support in the previous election was 3.5%, we
                calculate a proportionate improvement in Green support of
                3&times; (10.5 &#247; 3.5 = 3). For each region in the country,
                we then adjust the Green vote proportionally. In a region where
                Green support in the previous election was 5%, for instance,
                this 3&times; multiplier leads to a present support level of
                15%, so their new support in the region is set to be 15%. In a
                region where Green support in the previous election was 2%, the
                support would be set to 6%.
              </p>
              <p>
                The same process is repeated for all the other parties and their
                vote shares in all regions. (If the regional poll sliders are
                used instead, we skip calculating the proprotionate change
                nationally; the poll slider values are used directly.) Because
                given identical poll inputs, it will predict identical outcomes,
                it is <strong>deterministic</strong> &mdash; there is no
                randomness in the model.
              </p>
              <h5>Random swing model</h5>
              <p>
                This model works on a similar basis as the deterministic model.
                However, instead of applying a uniform swing to each riding in a
                region, the model will adjust the swing value by a random,
                statistically possible amount. This models the uncertainty of
                polling, which for typical polls will be correct within
                &plusmn;3.0%, 19 times out of 20 &mdash; which also means it
                will be outside of that range 1 time out of 20!
              </p>
              <p>
                To return to the previous example, this means that 19 times out
                of 20, the initial 10.5% Green polling value will actually
                randomly turn into a value between 8% and 13% &mdash; and 1 time
                out of 20, it will randomly turn into a value outside that
                range! Again, the same is true for the support levels of other
                parties. Because of this randomness, even identical poll numbers
                will lead to a variety of predicted outcomes.
              </p>
              <h4>Disclaimer</h4>
              <p>
                Please keep in mind that the projections are not necessarily an
                accurate reflection of voting intentions or actual results, and
                that there may be inaccuracies in the original data, its
                transformation, or its representation. Our model doesn&rsquo;t
                account for incumbency, star candidates, high-profile
                independents, or (currently) the results of by-elections since
                2015. The model also greatly simplifies a number of factors for
                ease of interpretation.
              </p>
              <p>
                In short, we recommend you use this tool for educational and
                entertainment purposes &mdash; not to corner the{' '}
                <NewTabLink href="https://predictionmarkets.ca/CA19.php">
                  UBC prediction market
                </NewTabLink>
                !
              </p>
              <h4>Credits</h4>
              <h5>Data sources</h5>
              <p>
                2015 election results are adapted from{' '}
                <NewTabLink href="https://www.elections.ca/content.aspx?section=res&dir=rep/off/42gedata&document=summary&lang=e">
                  figures published by Elections Canada
                </NewTabLink>
                . Electoral riding boundaries are adapted from{' '}
                <NewTabLink href="https://www.elections.ca/content.aspx?section=res&dir=rep/off/42gedata&document=summary&lang=e">
                  shape files also published by Elections Canada
                </NewTabLink>
                .
              </p>
              <h5>Frameworks</h5>
              <p>
                This app is built in{' '}
                <NewTabLink href="https://www.elections.ca/content.aspx?section=res&dir=rep/off/42gedata&document=summary&lang=e">
                  React
                </NewTabLink>{' '}
                (using{' '}
                <NewTabLink href="https://github.com/facebook/create-react-app">
                  Create React App
                </NewTabLink>
                ), and uses{' '}
                <NewTabLink href="https://redux.js.org/">Redux</NewTabLink> for
                state storage. The map is built using{' '}
                <NewTabLink href="https://leafletjs.com/">Leaflet</NewTabLink>.
              </p>
              <h4>Contact</h4>
              <p>
                We would love to hear your feedback, suggestions, or bug
                reports. Get in touch at{' '}
                <NewTabLink href="mailto:contact@plotandscatter.com.">
                  contact@plotandscatter.com
                </NewTabLink>
                .
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

export default AboutModal
